<template>
  <div :class="{'tabs--center':tabsAlign==='center','tabs--pills':mode==='pills','tabs--sm':size==='sm','tabs--vertical tabs--text-right':orientation==='vertical'}"
       class="tabs">
    <div class="tabs-list">
      <div v-clickoutside="$_onCloseDropDown"
           v-if="threshold > 0 && rest$.length > 0"
           :class="{'dropdown--open':dropdownActive$}"
           class="tabs-list__more dropdown"
           @click="$_toggleDropDown">
        <a class="dropdown__toggle"
           data-toggle="dropdown"
           aria-haspopup="true"
           aria-expanded="false">{{ mode === 'normal' ? more : '' }}</a>
        <div class="dropdown__menu"
             aria-labelledby="dropdownMenuButton">
          <a v-for="(tab,index) in rest$"
             :key="index"
             :checked="tab.name === activeName"
             class="dropdown__item"
             href="javascript:void(0)"
             @click="$_select(index)">{{ tab.label }}</a>
        </div>
      </div>
      <ul :class="listCls"
          class="nav">
        <li v-for="(tab,index) in list$"
            :key="index"
            role="presentation"
            class="nav-item">
          <a :class="{'active':tab.name === activeName,'disabled':tab.disabled}"
             href="javascript:void(0)"
             class="nav-link"
             :title="title?tab.label:''"
             @click="$_change(index)">{{ tab.label }}<i v-if="closeable$"
                                                        class="aid aid-close"
                                                        @click.stop="$_remove(index)"></i>
          </a>
        </li>
      </ul>
    </div>
    <div class="tab-content">
      <slot></slot>
    </div>
  </div>
</template>
<script>
import { isPInt } from '../../utils/assist'
import Locale from '../../mixins/locale'
import clickoutside from '../../directives/clickoutside'

export default {
  name: 'TTabs',
  directives: { clickoutside },
  mixins: [Locale],
  props: {
    title:{
      type:[String,Number,Boolean,Object,Array]
    },
    value: {
      type: String
    },
    tabsAlign: {
      type: String,
      default: 'left'
    },
    mode: {
      type: String,
      default: 'normal',
      validator (val) {
        return ['normal', 'pills'].indexOf(val) > -1
      }
    },
    orientation: {
      type: String,
      default: 'horizontal',
      validator (value) {
        return ['horizontal', 'vertical'].includes(value)
      }
    },
    size: {
      type: String,
      default: '',
      validator (value) {
        return ['', 'sm'].indexOf(value) > -1
      }
    },
    closeable: {
      type: Boolean,
      default: false
    },
    threshold: {
      type: Number,
      default: -1,
      validator: value => isPInt(value) || value === -1
    },
    beforeChange: {
      type: Function,
      default: null
    },
    beforeRemove: {
      type: Function,
      default: null
    },
    moreText: {
      type: String
    }
  },
  data () {
    return {
      activeName: '',
      activeIndex: -1,
      dropdownActive$: false,
      list$: [],
      rest$: [],
      closeable$: this.closeable
    }
  },
  computed: {
    listCls () {
      return {
        'nav-tabs': this.mode === 'normal',
        'nav-pills nav-justified': this.mode === 'pills'
      }
    },
    more () {
      if (this.moreText === undefined) {
        return this.t('t.tabs.more')
      } else {
        return this.moreText
      }
    }
  },
  watch: {
    value (val) {
      this.activeName = val
    },
    activeName (val) {
      this.$_updateActiveIndex()
      this.$_updatePanels()
    },
    threshold (val, oldVal) {
      if (val > 0 && val !== oldVal) {
        this.update()
      }
    },
    list$ (val) {
      if (this.closeable) {
        if (val.length === 1) {
          this.closeable$ = false
        } else {
          this.closeable$ = true
        }
      }
    }
  },
  created () {
    this.$on('tabs-refresh', () => {
      this.list$ = this.$_getTabs().map((v, i) => {
        return {
          label: v.label,
          icon: v.icon || '',
          name: v.name$ || i,
          disabled: v.disabled,
          index: i
        }
      })
      this.update()
    })
  },
  mounted () {
    if (this.value) {
      this.activeName = this.value
    } else if (this.$children.length > 0) {
      this.activeName = this.$children[0].name
    }
    this.$_updateActiveIndex()
  },
  methods: {
    // 获取所有的tab对象
    $_getTabs () {
      return this.$children.filter(
        v => v.$options._componentTag === 't-tab-panel'
      )
    },
    $_toggleDropDown () {
      this.dropdownActive$ = !this.dropdownActive$
    },
    $_onCloseDropDown () {
      this.dropdownActive$ = false
    },
    update () {
      if (this.list$.length >= this.threshold && this.threshold > 0) {
        this.rest$ = this.list$.slice(this.threshold)
        this.list$ = this.list$.slice(0, this.threshold)
      }
      if (!this.activeName && this.list$.length > 0) {
        this.activeName = this.list$[0].name
        this.$_updateActiveIndex()
      }
      this.$_updatePanels()
    },
    $_updatePanels () {
      const tabs = this.$_getTabs()
      tabs.forEach(tab => {
        tab.show$ = tab.name === this.activeName
      })
    },
    $_change (index) {
      const tab = this.list$[index]
      if (tab.disabled) {
        return
      }

      let oldName = this.activeName
      let oldIndex = this.activeIndex
      let newIndex = this.$_getActiveIndexByName(tab.name)

      if (this.beforeChange !== null) {
        if (this.beforeChange(tab.name, newIndex, oldName, oldIndex) === false) {
          return
        }
      }
      this.activeName = tab.name
      this.activeIndex = newIndex
      this.$emit('input', this.activeName)
      this.$emit('change', this.activeName, this.activeIndex, oldName, oldIndex)
    },
    $_select (index) {
      const tab = this.rest$[index]
      if (tab.disabled) {
        return
      }

      let oldName = this.activeName
      let oldIndex = this.activeIndex
      let newIndex = this.$_getActiveIndexByName(tab.name)

      if (this.beforeChange !== null) {
        if (this.beforeChange(tab.name, newIndex, oldName, oldIndex) === false) {
          return
        }
      }

      this.rest$ = [
        ...this.list$.slice(-1),
        ...this.rest$.filter((v, i) => i !== index)
      ]
      this.list$ = [tab, ...this.list$.slice(0, -1)]
      this.activeName = tab.name
      this.activeIndex = newIndex
      this.$emit('input', this.activeName)
      this.$emit('change', this.activeName, this.activeIndex, oldName, oldIndex)
    },
    $_remove (index) {
      const tab = this.list$[index]
      if (this.beforeRemove !== null) {
        if (this.beforeRemove(tab.name, index) === false) {
          return
        }
      }
      this.$_getTabs().forEach(v => {
        if (v.name$ === tab.name) {
          v.$el.parentNode.removeChild(v.$el)
          v.$destroy()
        }
      })
      // 当移除的是被选中的tab选择新的tab为选中状态
      if (tab.name === this.activeName) {
        const newTabs = this.list$.filter(v => !v.disabled)
        const rightNoDisabled = this.list$.filter(
          (v, i) => !v.disabled && i > index
        )
        const leftNoDisabled = this.list$.filter(
          (v, i) => !v.disabled && i < index
        )
        if (rightNoDisabled.length) {
          this.activeName = rightNoDisabled[0].name
        } else if (leftNoDisabled.length) {
          this.activeName = leftNoDisabled[0].name
        } else {
          this.activeName = newTabs.length > 0 ? newTabs[0].name : ''
        }
        this.$_updateActiveIndex()
      }
      this.list$.splice(index, 1)
      if (
        this.list$.length < this.threshold &&
        this.threshold > 0 &&
        this.rest$.length > 0
      ) {
        this.list$.push(this.rest$[0])
        this.rest$ = this.rest$.slice(1)
      }
      this.$emit('remove', index, tab.name)
    },
    $_getActiveIndexByName (activeName) {
      for (let i = this.$children.length - 1; i >= 0; i--) {
        if (this.$children[i].name === activeName) {
          return i
        }
      }
    },
    $_updateActiveIndex () {
      this.activeIndex = this.$_getActiveIndexByName(this.activeName)
    }
  }
}
</script>



// WEBPACK FOOTER //
// tabs.vue?59ad9f83